import moment from "moment-timezone";

// Window time can only be set when the check-in To time is 23:30
export const WINDOW_TIME_TO = "23:30";

const CHECKIN_DATE_FORMAT = "YYYY/MM/DD";

/**
 * Checks the facility's check-in time window against the current facility time.
 *
 * Without window_time the window is from–to of the same day. With window_time (1–4) and To at 23:30,
 * the window runs from "from" through midnight until To + N hours of the next day.
 *
 * checkinDate is the reservation check-in date guests may check in for right now (facility timezone).
 * During the after-midnight extension that is the previous day — the extension is reserved for late
 * arrivals, not for early check-in of today's reservations.
 *
 * Pass allowNextDay: false for pages that search reservations by today's date (check-in by name/phone QR):
 * they are blocked from midnight, since the after-midnight extension would expose today's reservations early.
 *
 * @param {{ from: string, to: string, window_time?: number }} timing
 * @param {string} timezone
 * @param {{ allowNextDay?: boolean }} [options]
 * @returns {{ isInRange: boolean, fromDisplay: string, toDisplay: string, checkinDate: string }}
 */
export function checkCheckinTimeWindow(timing, timezone, { allowNextDay = true } = {}) {
  const windowTime = timing.to === WINDOW_TIME_TO ? Number(timing.window_time) || 0 : 0;
  const facilityNow = moment.tz(timezone);
  const now = moment(facilityNow.format("HH:mm"), "HH:mm");
  const from = moment(timing.from, "HH:mm");
  const to = moment(timing.to, "HH:mm");
  const today = facilityNow.format(CHECKIN_DATE_FORMAT);

  if (windowTime === 0) {
    return {
      isInRange: now.isBetween(from, to, undefined, "[]"),
      fromDisplay: from.format("h:mm A"),
      toDisplay: to.format("h:mm A"),
      checkinDate: today,
    };
  }

  const nextDayEnd = to.clone().add(windowTime, "hours").subtract(1, "day");
  const isInExtension = allowNextDay && now.isSameOrBefore(nextDayEnd);

  return {
    isInRange: now.isSameOrAfter(from) || isInExtension,
    fromDisplay: from.format("h:mm A"),
    toDisplay: nextDayEnd.format("h:mm A"),
    checkinDate: isInExtension
      ? facilityNow.clone().subtract(1, "day").format(CHECKIN_DATE_FORMAT)
      : today,
  };
}
